import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { CompanyResponse } from '../../@shared/models/response.module';
import { CompanyService } from '../../@shared/services/company.service';
import { GeocodingService } from '../../@shared/services/geocoding.service';
import { MapMarker, MapConfig } from '../../@shared/components/location-map/location-map';
import { COR_COY_4 } from '../../@shared/models/input.module';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-organisations',
  standalone: false,
  templateUrl: './organisations.html',
  styleUrl: './organisations.css'
})
export class Organisations implements OnInit {
  companies: CompanyResponse[] = [];
  filteredCompanies: CompanyResponse[] = [];

  // Filters
  searchQuery: string = '';
  selectedType: string = '';
  sortByDistance: boolean = false;

  // Company types with colors (will be populated with translations)
  companyTypes: { value: string; label: string; color: string; icon?: string }[] = [];

  // User location
  userLocation: { lat: number; lng: number } | null = null;
  locationLoading: boolean = false;
  locationError: string | null = null;

  // Company coordinates cache (from geocoding)
  companyCoordinates: { [companyId: string]: { lat: number; lng: number } } = {};

  // Loading state
  loading: boolean = false;

  // Map properties
  mapMarkers: MapMarker[] = [];
  mapConfig: MapConfig = {
    height: '400px',
    mobileHeight: '300px',
    enableClustering: true,
    defaultCenter: { lat: 59.9139, lng: 10.7522 }, // Oslo, Norway
    defaultZoom: 5
  };
  geocodingInProgress: boolean = false;

  constructor(
    private companyService: CompanyService,
    private geocodingService: GeocodingService,
    private router: Router,
    private route: ActivatedRoute,
    private translateService: TranslateService
  ) {}

  private readFiltersFromUrl() {
    const params = this.route.snapshot.queryParams;
    if (params['search']) this.searchQuery = params['search'];
    if (params['type']) this.selectedType = params['type'];
    if (params['sortByDistance'] === 'true') this.sortByDistance = true;
  }

  private updateUrlWithFilters() {
    const queryParams: any = {};
    if (this.searchQuery) queryParams.search = this.searchQuery;
    if (this.selectedType) queryParams.type = this.selectedType;
    if (this.sortByDistance) queryParams.sortByDistance = 'true';

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: '',
      replaceUrl: true
    });
  }

  ngOnInit() {
    this.readFiltersFromUrl();
    this.initCompanyTypes();
    this.loadCompanies();
    this.getUserLocation();

    // Re-initialize company types when language changes
    this.translateService.onLangChange.subscribe(() => {
      this.initCompanyTypes();
    });
  }

  private initCompanyTypes() {
    this.companyTypes = [
      { value: '', label: this.translateService.instant('organisations.allTypes'), color: '' },
      { value: 'stable', label: this.translateService.instant('organisations.typeStable'), color: 'orange', icon: 'fa-warehouse' },
      { value: 'riding_club', label: this.translateService.instant('organisations.typeRidingClub'), color: 'blue', icon: 'fa-users' },
      { value: 'riding_school', label: this.translateService.instant('organisations.typeRidingSchool'), color: 'purple', icon: 'fa-graduation-cap' }
    ];
  }

  loadCompanies() {
    this.loading = true;
    const params: COR_COY_4 = {
      compact: true  // Use compact mode for ~60% smaller payload
    };

    // Use public endpoint (no auth required) with compact mode
    this.companyService.getPublicCompanies(params).subscribe({
      next: (response) => {
        // Filter out stores_services organisations (they are shown on /stores-services page)
        this.companies = (response.data || []).filter(
          company => company.company_type !== 'stores_services'
        );
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  applyFilters() {
    this.filteredCompanies = this.companies.filter(company => {
      // Type filter
      if (this.selectedType && company.company_type !== this.selectedType) {
        return false;
      }

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        const matchesSearch =
          company.company_name?.toLowerCase().includes(query) ||
          company.description?.toLowerCase().includes(query) ||
          company.city?.toLowerCase().includes(query) ||
          company.country?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      return true;
    });

    // Geocode companies for map
    this.geocodeCompanies();
  }

  onTypeChange() {
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  getMarkerColor(companyType: string): 'red' | 'blue' | 'yellow' | 'purple' | 'orange' | 'pink' {
    const typeConfig = this.companyTypes.find(t => t.value === companyType);
    return (typeConfig?.color as any) || 'red';
  }

  getCompanyTypeLabel(companyType: string): string {
    const typeConfig = this.companyTypes.find(t => t.value === companyType);
    return typeConfig?.label || companyType || 'Unknown';
  }

  getCompanyTypeIcon(companyType: string): string {
    const typeConfig = this.companyTypes.find(t => t.value === companyType);
    return typeConfig?.icon || 'fa-building';
  }

  async geocodeCompanies() {
    this.geocodingInProgress = true;
    this.mapMarkers = [];

    const companiesToGeocode = this.filteredCompanies.filter(
      company => company.address || company.city || company.country
    );

    // Debug: log first company to check address fields
    if (companiesToGeocode.length > 0) {
      const first = companiesToGeocode[0];
      console.log('[Geocode Debug] First organisation address fields:', {
        company_name: first.company_name,
        address: first.address,
        postal_code: first.postal_code,
        city: first.city,
        country: first.country
      });
    }

    for (const company of companiesToGeocode) {
      const result = await this.geocodingService.geocodeAddress(
        company.address || '',
        company.city || '',
        company.postal_code || '',
        company.country || ''
      );

      if (result.success && result.lat && result.lng) {
        // Cache coordinates for distance calculation
        this.companyCoordinates[company.company_id] = { lat: result.lat, lng: result.lng };

        // Calculate distance for popup display
        let distanceStr: string | undefined;
        if (this.userLocation) {
          const distKm = this.calculateDistance(
            this.userLocation.lat,
            this.userLocation.lng,
            result.lat,
            result.lng
          );
          distanceStr = this.formatDistance(distKm);
        }

        this.mapMarkers = [...this.mapMarkers, {
          id: company.company_id,
          lat: result.lat,
          lng: result.lng,
          title: company.company_name || 'Unknown Organisation',
          subtitle: this.formatCompanySubtitle(company),
          imageUrl: company.company_logo_url || company.header_image_url || undefined,
          color: this.getMarkerColor(company.company_type || ''),
          distance: distanceStr,
          data: company
        }];
      }
    }

    this.geocodingInProgress = false;

    // Re-sort if sorting by distance is enabled
    if (this.sortByDistance && this.userLocation) {
      this.sortCompaniesByDistance();
    }
  }

  private sortCompaniesByDistance() {
    if (!this.userLocation) return;

    this.filteredCompanies.sort((a, b) => {
      const distA = this.getCompanyDistance(a);
      const distB = this.getCompanyDistance(b);
      if (distA === null && distB === null) return 0;
      if (distA === null) return 1;
      if (distB === null) return -1;
      return distA - distB;
    });
  }

  private formatCompanySubtitle(company: CompanyResponse): string {
    const parts = [];
    if (company.city) parts.push(company.city);
    if (company.country) parts.push(company.country);
    return parts.join(', ');
  }

  onMapMarkerClick(marker: MapMarker) {
    // No scroll behavior - user can click "View Details" in the info window
  }

  onMapInfoWindowAction(marker: MapMarker) {
    this.router.navigate(['/organisations', marker.id]);
  }

  onSearchChange() {
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  clearFilters() {
    this.searchQuery = '';
    this.selectedType = '';
    this.sortByDistance = false;
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  toggleSortByDistance() {
    this.sortByDistance = !this.sortByDistance;
    this.applyFilters();
    this.updateUrlWithFilters();
  }

  getUserLocation() {
    if (!navigator.geolocation) {
      return;
    }

    this.locationLoading = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this.userLocation = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        this.locationLoading = false;
      },
      () => {
        this.locationLoading = false;
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }

  calculateDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const R = 6371;
    const dLat = this.toRad(lat2 - lat1);
    const dLng = this.toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  getCompanyDistance(company: CompanyResponse): number | null {
    if (!this.userLocation) return null;
    const coords = this.companyCoordinates[company.company_id];
    if (!coords) return null;
    return this.calculateDistance(
      this.userLocation.lat,
      this.userLocation.lng,
      coords.lat,
      coords.lng
    );
  }

  formatDistance(km: number | null): string {
    if (km === null) return '';
    if (km < 1) return `${Math.round(km * 1000)} m`;
    return `${Math.round(km)} km`;
  }

  getCompanyInitials(company: CompanyResponse): string {
    if (!company.company_name) return '?';
    const words = company.company_name.split(' ');
    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
    return company.company_name.substring(0, 2).toUpperCase();
  }
}
